import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/ui-kit/utils';

const badgeVariants = cva(
  `focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:border-destructive
  aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 inline-flex w-fit shrink-0
  items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-full border px-3 py-1
  transition-[color,box-shadow] focus-visible:ring-[3px] [&>svg]:pointer-events-none [&>svg]:size-3`,
  {
    variants: {
      variant: {
        default: 'bg-card text-foreground [a&]:hover:bg-primary/90 border-transparent',
        success: 'bg-success-trans text-success [a&]:hover:bg-success/90 border-transparent',
        primary: 'bg-primary text-primary-foreground [a&]:hover:bg-primary/90 border-transparent',
        secondary:
          'bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90 border-transparent',
        destructive: `bg-destructive focus-visible:ring-destructive/20 dark:bg-destructive/60
        dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90 border-transparent text-white`,
        outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function Badge({
  className,
  variant,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot : 'span';

  return (
    <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
